/**
 * Drizzle mutations (PowerSync local-first) of the Vigía fleet domain (P-6).
 *
 * Every write goes to local SQLite; PowerSync uploads it in background and the server
 * (`SyncRepository`) re-validates permissions (owner or org member, CC-40).
 * `user_id` + `organization_id` are DENORMALIZED in every row (requirement of the
 * sync rules). Parts are APPEND-ONLY and sealed; they are never edited.
 */

import {
  device,
  device_alert,
  device_part,
  type PowerSyncDrizzleDB,
  remote_control_session,
} from "@vigia/powersync";
import type { SQLiteColumn } from "drizzle-orm/sqlite-core";
import { nowISO } from "../utils/date-helpers";
import { generateId } from "@fleet/utils";
import { collectAndSeal } from "./collector";
import { evaluate } from "./alerts";
import {
  patchRowVerified,
  type ScopeCondition,
  type VerifiedWriteOptions,
} from "../powersync/verified-write";

interface FleetOwner {
  userId: string;
  organizationId?: string | null;
}

/**
 * Tenant isolation scope (§4.14) — a SINGLE place that knows how to build
 * `[{ column: organization_id, value }]`, reused by every `UPDATE` in this
 * module. `device`/`device_alert`/`remote_control_session` sync ALL
 * the user's organizations at once (`infra/sync_streams.yaml`,
 * `org_fleet`), so an `id` that arrives manipulated or out of sync is not enough
 * as a filter: the row must also belong to the organization the caller
 * believes it is touching. `organizationId` travels as an explicit
 * parameter in every mutation — it is never guessed in here.
 */
function orgScope(
  column: SQLiteColumn,
  organizationId: string | null | undefined,
): ScopeCondition[] {
  return [{ column, value: organizationId ?? null }];
}

/**
 * Enrolls (creates) or updates a device in the fleet.
 *
 * PowerSync NOTE: local tables are SQLite VIEWS (INSTEAD OF triggers), which
 * support INSERT/UPDATE but NOT `UPSERT`/`ON CONFLICT` ("cannot UPSERT a view").
 *
 * §4.11 lesson 1 — why this NO LONGER decides INSERT-vs-UPDATE with a prior
 * SELECT: a `SELECT … WHERE id = ?` against the view can return «does not exist»
 * during the ghost window (the view is rebuilt from the checkpoint as the
 * CRUD queue drains) even though the device DOES exist. Deciding with that
 * SELECT leads to re-inserting it with a clean `INSERT` — losing `archived` and
 * `enrolled_at` instead of duplicating the row (the id is the same), which is a
 * different failure mode but just as silent: an archived device
 * would reappear active.
 *
 * That is why the decision is NOT guessed here: the caller makes it via `isNew`
 * (`reportInventoryMutation` already knows whether it generated the id or was given an
 * existing one). With `isNew === false` the write is a `patchRowVerified`
 * —waits/re-reads/retries— instead of a race against the window.
 */
export async function enrollDeviceMutation(
  db: PowerSyncDrizzleDB,
  input: FleetOwner & {
    id?: string;
    /** `false` = the caller KNOWS this id already exists: verified UPDATE. */
    isNew?: boolean;
    name: string;
    hostname?: string | null;
    platform?: string | null;
    agentVersion?: string | null;
  },
  options: VerifiedWriteOptions = {},
): Promise<string> {
  const now = nowISO();

  if (input.id && input.isNew === false) {
    await patchRowVerified(
      db,
      device,
      device.id,
      input.id,
      {
        name: input.name,
        hostname: input.hostname ?? null,
        platform: input.platform ?? null,
        agent_version: input.agentVersion ?? null,
        last_seen_at: now,
        updated_at: now,
      },
      { ...options, scope: orgScope(device.organization_id, input.organizationId) },
    );
    return input.id;
  }

  const id = input.id ?? generateId();
  await db.insert(device).values({
    id,
    user_id: input.userId,
    organization_id: input.organizationId ?? null,
    name: input.name,
    hostname: input.hostname ?? null,
    platform: input.platform ?? null,
    agent_version: input.agentVersion ?? null,
    enrolled_at: now,
    last_seen_at: now,
    archived: 0,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * 🎯 The browser agent REPORTS its inventory: collects + seals a part,
 * enrolls it if needed, uploads the part and materializes threshold alerts.
 * Returns the device_id, the part version and the alerts fired.
 */
export async function reportInventoryMutation(
  db: PowerSyncDrizzleDB,
  input: FleetOwner & { deviceId?: string; name?: string; version?: number },
  options: VerifiedWriteOptions = {},
): Promise<{ deviceId: string; version: number; alerts: number }> {
  // This branch is the ONLY source of truth on whether the id is new or not:
  // `enrollDeviceMutation` no longer guesses it with a SELECT (§4.11 lesson 1).
  const isNewDevice = input.deviceId === undefined;
  const deviceId = input.deviceId ?? generateId();
  const version = input.version ?? 1;
  const part = await collectAndSeal(deviceId, version);

  // 1) Enroll/refresh the device.
  await enrollDeviceMutation(
    db,
    {
      id: deviceId,
      isNew: isNewDevice,
      userId: input.userId,
      organizationId: input.organizationId ?? null,
      name: input.name ?? (part.inventory.os.hostname || "Este dispositivo"),
      hostname: part.inventory.os.hostname || null,
      platform: part.inventory.os.name || null,
      agentVersion: "web-0.1.0",
    },
    options,
  );

  // 2) Upload the sealed part (append-only).
  await db.insert(device_part).values({
    id: generateId(),
    device_id: deviceId,
    user_id: input.userId,
    organization_id: input.organizationId ?? null,
    version,
    seal: part.seal,
    inventory: JSON.stringify(part.inventory),
    collected_at: part.collected_at,
    created_at: nowISO(),
  });

  // 3) Materialize the threshold alerts fired by this inventory.
  const raised = evaluate(part.inventory);
  await Promise.all(
    raised.map((al) =>
      db.insert(device_alert).values({
        id: generateId(),
        device_id: deviceId,
        user_id: input.userId,
        organization_id: input.organizationId ?? null,
        rule_id: al.rule_id,
        severity: al.severity,
        metric: al.metric,
        value: al.value,
        threshold: al.threshold,
        message: al.message,
        raised_at: nowISO(),
        resolved_at: null,
      }),
    ),
  );

  return { deviceId, version, alerts: raised.length };
}

/**
 * Marks an alert as resolved.
 *
 * Verified (§4.11): an alert is typically fired and resolved in the
 * same session — precisely the window in which the row can still not appear
 * in the rebuilt view.
 *
 * `organizationId` (§4.14): the organization the caller BELIEVES
 * this alert belongs to (normally, the one of the `device` loaded on screen).
 * It travels as `scope` — if the real row is from another organization, the write does not
 * land (see the security note in `verified-write.ts`).
 */
export async function resolveAlertMutation(
  db: PowerSyncDrizzleDB,
  alertId: string,
  organizationId: string | null | undefined,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(
    db,
    device_alert,
    device_alert.id,
    alertId,
    { resolved_at: nowISO() },
    { ...options, scope: orgScope(device_alert.organization_id, organizationId) },
  );
}

/**
 * Archives (retires) a device from the fleet. Verified (§4.11).
 *
 * `organizationId` (§4.14): isolation scope — see `resolveAlertMutation`.
 */
export async function archiveDeviceMutation(
  db: PowerSyncDrizzleDB,
  deviceId: string,
  organizationId: string | null | undefined,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(
    db,
    device,
    device.id,
    deviceId,
    { archived: 1, updated_at: nowISO() },
    { ...options, scope: orgScope(device.organization_id, organizationId) },
  );
}

// `ControlScope` is the same as the remote control protocol's (single source).
export type { ControlScope } from "./remote/protocol";
import type { ControlScope } from "./remote/protocol";
/** Consent state as it is PERSISTED in `remote_control_session`
 * (superset of the in-memory FSM: adds `ended` when the session closes). */
export type SessionConsentState = "requested" | "granted" | "denied" | "revoked" | "ended";

/**
 * Opens a remote control session in state `requested` (requests consent).
 * The P2P transport (WebRTC) is an infra gap (🔶): here the consent
 * protocol + its synced state is modeled; every real action is
 * audited in the hash-chain via API (`recordRemoteAction`).
 */
export async function requestRemoteControlMutation(
  db: PowerSyncDrizzleDB,
  input: FleetOwner & {
    deviceId: string;
    controllerId: string;
    scope: ControlScope;
    reason?: string;
  },
): Promise<string> {
  const id = generateId();
  await db.insert(remote_control_session).values({
    id,
    device_id: input.deviceId,
    controller_id: input.controllerId,
    user_id: input.userId,
    organization_id: input.organizationId ?? null,
    scope: input.scope,
    consent_state: "requested",
    reason: input.reason ?? null,
    started_at: nowISO(),
    ended_at: null,
  });
  return id;
}

/**
 * Resolves a session's consent (granted/denied).
 *
 * Verified (§4.11): the session is opened with `requestRemoteControlMutation` and
 * decided seconds later, in the same interaction — the exact window in which the
 * newly created row can still not appear in the view.
 *
 * `organizationId` (§4.14): isolation scope — see `resolveAlertMutation`.
 */
export async function decideConsentMutation(
  db: PowerSyncDrizzleDB,
  sessionId: string,
  state: Extract<SessionConsentState, "granted" | "denied">,
  organizationId: string | null | undefined,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(
    db,
    remote_control_session,
    remote_control_session.id,
    sessionId,
    { consent_state: state, ended_at: state === "denied" ? nowISO() : null },
    { ...options, scope: orgScope(remote_control_session.organization_id, organizationId) },
  );
}

/**
 * Ends a remote control session. Verified (§4.11).
 *
 * `organizationId` (§4.14): isolation scope — see `resolveAlertMutation`.
 */
export async function endRemoteControlMutation(
  db: PowerSyncDrizzleDB,
  sessionId: string,
  organizationId: string | null | undefined,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  await patchRowVerified(
    db,
    remote_control_session,
    remote_control_session.id,
    sessionId,
    { consent_state: "ended", ended_at: nowISO() },
    { ...options, scope: orgScope(remote_control_session.organization_id, organizationId) },
  );
}
